import React, { useEffect, useState, useMemo } from 'react';
import { Grid, Box, CircularProgress, Typography } from '@mui/material';
import PhotoCard from '../PhotoCard/PhotoCard';
import PhotoPreviewModal from '../PhotoPreviewModal/PhotoPreviewModal';
import imageService from '../../services/imageService';

const getImageFilename = (img) =>
  img.name || (typeof img.key === 'string' ? img.key.split('/').pop() : img.key);

const ImageGallery = ({ customer, event, images, onImageSelect, selectedImages, brandedContentSettings, brandedContentImage }) => {
  const [loading, setLoading] = useState(true);
  const [processedImages, setProcessedImages] = useState([]);
  const [previewModal, setPreviewModal] = useState({
    open: false,
    photo: null
  });

  // Ensure selectedImages is always an array
  const safeSelectedImages = selectedImages || [];

  // Memoize the image list to prevent unnecessary processing
  const imageList = useMemo(() => {
    if (!images || images.length === 0) return [];
    return [...images].sort((a, b) => {
      const ta = a.lastModified != null ? new Date(a.lastModified).getTime() : 0;
      const tb = b.lastModified != null ? new Date(b.lastModified).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return String(b.key || '').localeCompare(String(a.key || ''));
    });
  }, [images]);

  useEffect(() => {
    const processImages = async () => {
      try {
        if (!images || images.length === 0) {
          setProcessedImages([]);
          setLoading(false);
          return;
        }

        // Find only the new images that haven't been processed
        const newImages = images.filter((img) => {
          return !imageService.isProcessed(customer, event, getImageFilename(img));
        });

        if (newImages.length > 0) {
          setLoading(true);
          await imageService.processImageList(
            customer,
            event,
            newImages.map((img) => getImageFilename(img))
          );
        }
        
        // Update the processed images list with all images
        setProcessedImages(imageList);
      } catch (error) {
        console.error('Error processing images:', error);
      } finally {
        setLoading(false);
      }
    };

    processImages();
  }, [customer, event, images, imageList]);

  const handlePreview = (photo) => {
    setPreviewModal({
      open: true,
      photo
    });
  };

  const handleClosePreview = () => {
    setPreviewModal({
      open: false,
      photo: null
    });
  };

  const handleShare = (photo) => {
    if (onImageSelect) {
      onImageSelect(photo);
    }
  };

  // Only show loading indicator for new images
  if (loading && processedImages.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        p: 3,
        minHeight: 'calc(100vh - 200px)' // Account for header and padding
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!images || images.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        p: 3,
        minHeight: 'calc(100vh - 200px)', // Account for header and padding
        width: '100%'
      }}>
        <Typography variant="h6" color="text.secondary">
          No photos available for this event
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid 
        container 
        spacing={2}
      >
        {processedImages.map((image) => {
          const isSelected = safeSelectedImages?.some(img => img.key === image.key);
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={image.key}>
              <PhotoCard
                item={image}
                onSelect={onImageSelect ? () => onImageSelect(image) : undefined}
                onShare={handleShare}
                onPreview={handlePreview}
                isSelected={isSelected}
                brandedContentSettings={brandedContentSettings}
                brandedContentImage={brandedContentImage}
              />
            </Grid>
          );
        })}
      </Grid>

      <PhotoPreviewModal
        open={previewModal.open}
        onClose={handleClosePreview}
        photo={previewModal.photo}
        onShare={handleShare}
        brandedContentSettings={brandedContentSettings}
        brandedContentImage={brandedContentImage}
      />
    </>
  );
};

export default ImageGallery; 